import { useState } from 'react';

import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { AppointmentRecurring, MatchingProvidersType } from '@uplift/api';
import { useTimezone } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import { ScheduleSessionModal, SelectedProvider } from '@uplift/pages';
import { useInsuranceCompaniesQuery, useProviderPublicMatchingProfile } from '@uplift/queries';
import { DateObject } from '@uplift/services';
import { ProviderMedicalRole } from '@uplift/types';
import { Alert, ScrollableView } from '@uplift/ui-v2';
import lodashConcat from 'lodash/concat';

import { MatchScheduleSessionData, OnboardingMachineContext } from '../types';

export type MatchProps = {
  onScheduleSession: (data: MatchScheduleSessionData) => void;
  selectedProviderId: string;
  stateId: string;
  insuranceId: string;
  specialtyIds: string[];
  isSlotUnavailable: boolean;
  identityFilters?: OnboardingMachineContext['identityFilters'];
  experienceFilters?: OnboardingMachineContext['experienceFilters'];
  matchingPaymentType: MatchingProvidersType;
  isBookingSponsored?: boolean;
  isStudentSecondBooking?: boolean;
};

export const Match: React.VFC<MatchProps> = (props) => {
  const l10n = useL10n();
  const timezone = useTimezone();
  const { classes } = useMatchStyles();
  const [ modalSelectedSlot, setModalSelectedSlot ] = useState<DateObject | null>(null);
  const { data: publicProfile } = useProviderPublicMatchingProfile(props.selectedProviderId);
  const { data: companies } = useInsuranceCompaniesQuery();
  const medicalRole = publicProfile?.user?.medicalRole ?? ProviderMedicalRole.therapist;

  const onScheduleSession = () => {
    if (modalSelectedSlot) {
      props.onScheduleSession({
        appointment: {
          fromDatetime: modalSelectedSlot.toISOString(),
          toDatetime: modalSelectedSlot.add(60, 'minutes').toISOString(),
          timezone: timezone,
          recurring: {
            [ProviderMedicalRole.therapist]: AppointmentRecurring.weekly,
            [ProviderMedicalRole.psychiatrist]: AppointmentRecurring.none,
          }[medicalRole],
        }
      });
    }
  };

  const matchedInsuranceCompany = props.matchingPaymentType === MatchingProvidersType.cashpay
    ? null
    : companies?.find(c => c.id === props.insuranceId) ?? null;

  return (
    <ScrollableView>
      <div className={classes.root}>
        {modalSelectedSlot && (
          <ScheduleSessionModal
            matchedInsuranceCompany={matchedInsuranceCompany}
            providerItem={publicProfile}
            selectedScheduleSessionDate={modalSelectedSlot}
            onClose={() => setModalSelectedSlot(null)}
            onScheduleSession={onScheduleSession}
            isBookingSponsored={props.isBookingSponsored}
            isStudentSecondBooking={props.isStudentSecondBooking}
          />
        )}

        {props.isSlotUnavailable && (
          <Box p={2} bgcolor="#fff" display="flex" justifyContent="center">
            <Box width="972px">
              <Alert severity="info" title={l10n.matching.selectedProvider.rebookAlertTitle}>
                <span dangerouslySetInnerHTML={{ __html: l10n.matching.selectedProvider.rebookAlertContent }} />
              </Alert>
            </Box>
          </Box>
        )}

        <SelectedProvider
          selectedProvider={props.selectedProviderId}
          isScrollableView
          onScheduleSession={setModalSelectedSlot}
          selectedSpecialtyIds={props.specialtyIds}
          selectedExperienceIds={lodashConcat(
            props.experienceFilters?.communityIds,
            props.experienceFilters?.ethnicityIds,
            props.experienceFilters?.faithIds
          )}
          selectedIdentityIds={lodashConcat(
            props.identityFilters?.ethnicityIds,
            props.identityFilters?.genderIds,
            props.identityFilters?.faithIds
          )}
          matchedInsuranceCompany={matchedInsuranceCompany}
          medicalRole={medicalRole}
          stateId={props.stateId}
          hideAcceptedInsurances={props.isBookingSponsored}
        />
      </div>
    </ScrollableView>
  );
};

const useMatchStyles = makeStyles({ name: 'Match' })({
  root: {
    boxSizing: 'border-box',
  },
});
